/**
 * pendingEs.js — PENDING_ES: what a Spanish-speaking patient reads while a new English
 * string waits for Dr. Rami (CLIENT_PUSH_PLAN 2026-10-07, decision 5).
 *
 * RULE SINCE 2026-10-18 (Alex, standing): a prepared Spanish translation ships right away in es.json;
 * Dr. Rami reviews it afterwards and his corrections ship in a later push. Nothing new needs to be
 * added to the lists below for a string whose Spanish we have. The older text below is the history
 * (the lists still hold keys with NO Spanish at all, see below).
 *
 * THE OLD RULE (until 2026-10-18). Every Spanish string a patient reads is approved by Dr. Rami. A new English
 * string ships in en.json only; es.json is not touched until he approves the Spanish.
 * Without this file, t() falls back to ENGLISH for a key es.json lacks (src/i18n/index.jsx),
 * so a Spanish patient would read an unapproved English sentence in a Spanish app.
 *
 * THE OWNER. One map from a new key to an existing, approved es key that says the nearest
 * true thing. t() reads it only when the language is 'es' AND es.json lacks the key; the
 * day the approved Spanish lands in es.json the entry is removed (check-i18n-keys ARM 5
 * fails while both exist), and nothing else changes.
 *
 * PENDING_ES_WITHHELD. New keys with no honest existing sentence to stand in for them. They are
 * never drawn under es: the screen asks hasText(key) (src/i18n/index.jsx) and, under es, keeps
 * today's screen. Removed from this list the day the approved Spanish lands in es.json.
 *
 * Pure module, no imports: the gates import it directly.
 */

export const PENDING_ES = Object.freeze({
  // EMPTY since s99g (2026-10-11): Dr. Rami approved the Spanish of the two PROD-690
  //   modality_not_offered sentences; they are in es.json. Add a stand-in here only for an English
  //   key whose Spanish is not approved yet.
})

export const PENDING_ES_WITHHELD = Object.freeze([
  // EMPTIED at s99g (2026-10-11): Dr. Rami approved the Spanish of all 26 withheld keys then here
  //   (sendReport.faxFound, eligibility.limit* x3, myInsurance.* x22); they are in es.json.
  //   myInsurance's screen is still behind src/config/insuranceLevel1.js (OFF).
  // Added back at the train merge (2026-10-11): the REP-10 and PROD-698 keys below are NOT in
  //   that approval; their Spanish still needs Dr. Rami.
  // REP-10 (v242.251.0): "Report a problem" (Settings → Help & Support, the crash screen). English
  //   drafted for Alex; Spanish needs Dr. Rami. Under es: no Help & Support row (SettingsScreen
  //   asks hasText) and today's crash screen (CrashFallback asks hasText). The feature is also
  //   behind REPORT_PROBLEM_ENABLED (src/config/featureFlags.js), which is OFF.
  // PROD-802 (B802): the app's own refusal sentences for the support-ticket and attachment routes
  //   (support.errCheckForm/errSession/errAccount/errFailed/errMessageTooLong and the five
  //   attachErr* mapped by utils/bugReport.js) have Spanish in es.json (our usted translation; Dr. Rami reviews, list 6).
  // PROD-1159 (2026-10-19): crash.title / body / tryAgain / home / report / reportSignedOut have their
  //   Spanish in es.json (our usted translation, Alex's 2026-10-18 rule; Dr. Rami reviews after, list 5).
  //   Under es the crash screen is CrashFallback; its in-app report stays English-free (CrashFallback asks
  //   hasText('support.modalTitle') and offers the website form while support.* is withheld).
  // PROD-698: the check-in vitals refusal (the API's own sentence; English from Alex's 2026-10-07
  //   answer, Spanish needs Dr. Rami). Under es no field note and no client-side block (CheckInModal
  //   asks hasText): an API refusal reads the approved generic checkIn.checkInFailed.
  'checkIn.vitalInvalid',
  // MEET-patient PA0 (PROD-538, 2026-10-12): the recording question for a video visit. English is
  //   counsel's draft (R-M2); the Spanish below is a DRAFT for Dr. Rami (list 4, R-M3) and is NOT
  //   in es.json. Under es the question is not drawn (canAskRecordingConsent asks hasText) and the
  //   visit is booked without a decision = unrecorded (Q-M4). DRAFT es (usted):
  //     consentTitle   "Esta consulta se grabará"
  //     consentBody    "Con su permiso, su médico grabará y transcribirá esta videoconsulta para
  //                     completar las notas de su visita. La grabación se guarda de forma segura
  //                     como parte de su expediente médico."  (R-M3 wording)
  //     bookingAgree   "Aceptar la grabación"
  //     bookingDecline "Continuar sin grabación"
  'videoVisit.consentTitle',
  'videoVisit.consentBody',
  'videoVisit.bookingAgree',
  'videoVisit.bookingDecline',
  // MEET-patient PA1 (PROD-538, 2026-10-12): the pre-join sheet of JoinVideoVisit (MEET_PLAN 3.7).
  //   Under es the sheet is not drawn and the link opens without a decision = unrecorded; the Join
  //   button shows the approved diagnose.joinVideoCall ("Unirse a la videollamada"; PA3 retired
  //   visits.joinZoomMeeting). DRAFT es (usted) for Dr. Rami, list 4:
  //     agreeAndJoin         "Aceptar y unirse"            (R-M3 wording)
  //     joinWithoutRecording "Unirse sin grabación"        (R-M3 wording)
  //     notRecorded          "Su consulta no se grabará."
  //     saveFailed           "No pudimos guardar su elección. Por favor, inténtelo de nuevo."
  //     visits.joinVideoVisit "Unirse a la videoconsulta"
  'videoVisit.agreeAndJoin',
  'videoVisit.joinWithoutRecording',
  'videoVisit.notRecorded',
  'videoVisit.saveFailed',
  'visits.joinVideoVisit',
  // MEET-patient PA3 (PROD-538, PROD-674 / F-M8, 2026-10-12): the hint under a Google Meet visit's
  //   Join button. Under es it is not drawn. DRAFT es (usted) for Dr. Rami, list 4:
  //     opensInGoogleMeet "Su consulta se abrirá en Google Meet."
  'videoVisit.opensInGoogleMeet',
  // PROD-1108 (shipped 2026-10-18): videoVisit.iosOpenApp now has its Spanish in es.json (see the 2026-10-18 rule above).
  // P20 (Q28, 2026-10-14): the line under the booked visit while a reschedule waits for the physician.
  //   English approved by Alex; Spanish needs Dr. Rami. Under es Case Home keeps today's line
  //   (CaseHome asks hasText).
  'caseHome.changeRequested',
  // PROD-940 (2026-10-17): the check-in copay card words moved from literals into keys. English
  //   is byte-identical to the old literals; Spanish needs Dr. Rami. Under es the card reads
  //   English, exactly as it did before (the literals were English under es too).
  'checkIn.copayCard.verifiedCopay',
  'checkIn.copayCard.estimatedCopay',
  'checkIn.copayCard.verifiedBadge',
  'checkIn.copayCard.officeVisit',
  'checkIn.copayCard.specialist',
  'checkIn.copayCard.labelDueAtVisit',
  'checkIn.copayCard.confirmedBy',
  'checkIn.copayCard.basedOnPlan',
  'checkIn.copayCard.notOnFile',
  'checkIn.copayCard.finalMayVary',
  // PROD-936 NEW-1/NEW-2, PROD-1132 later button + hint (shipped 2026-10-18): eligibility.reusedNotice,
  //   onboarding.insurance.cardHint / cardLater / cardLaterHint have their Spanish in es.json.
  // PROD-1089 step 2 and PROD-1131 (shipped 2026-10-18): appointment.errPhysicianInactive,
  //   appointment.concernSentPhysicianInactive and cob.mbiLimit have their Spanish in es.json.
  // PROD-1133d (2026-10-18): cob.secondaryReusedLine has its Spanish in es.json (usted; Dr. Rami reviews after).
])

const lookup = (dict, key) => key.split('.').reduce((acc, part) => (acc && typeof acc === 'object' ? acc[part] : undefined), dict)

/**
 * The approved text for `key` in `language`, or undefined. en: en.json. es: es.json, else the
 * PENDING_ES stand-in. Any other language: its own file only. Never another language's text.
 */
export function approvedText(translations, language, key) {
  const own = lookup(translations[language], key)
  if (typeof own === 'string') return own
  if (language === 'es' && Object.prototype.hasOwnProperty.call(PENDING_ES, key)) {
    const stand = lookup(translations.es, PENDING_ES[key])
    if (typeof stand === 'string') return stand
  }
  return undefined
}
